import React, { useEffect, useState, useRef, useCallback } from 'react'
import rawDataService, { UploadBatchItem } from '../services/rawDataService'
import { trackPackaging, trackedKeys } from '../services/packagingWatcher'
import systemService, { TriggerLabelSchemas } from '../services/systemService'
import { parseUTCDate } from '../utils/date'

interface Props {
  nodeId: number
  uploadMethod?: 'manual' | 'auto'
  onSelectBatch: (batch: UploadBatchItem | null) => void
  selectedBatchId?: number | null
  onResume?: (filename: string) => void
  onAfterDelete?: (deletedBatchId: number) => void
  readOnly?: boolean
  canDelete?: boolean
  canDownload?: boolean
}

export default function UploadHistorySection({
  nodeId,
  uploadMethod = 'manual',
  onSelectBatch,
  selectedBatchId,
  onResume,
  onAfterDelete,
  readOnly = false,
  canDelete = true,
  canDownload = true,
}: Props) {
  const [batches, setBatches] = useState<UploadBatchItem[]>([])
  const [skip, setSkip] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const pageSize = 50
  const [activeSession, setActiveSession] = useState<{ session_id: string; batch_id?: number | null; node_id?: number | null } | null>(null)
  const lastSessionIdRef = useRef<string | null>(null)
  const [triggerSchemas, setTriggerSchemas] = useState<TriggerLabelSchemas>({})

  useEffect(() => {
    systemService.getTriggerLabels().then(setTriggerSchemas).catch(() => {})
  }, [])

  useEffect(() => {
    // 노드가 바뀌면 초기화 후 첫 페이지 로드
    setBatches([])
    setSkip(0)
    setHasMore(true)
    onSelectBatch(null)
    // 이전 nodeId의 응답이 뒤늦게 도착해 현재 nodeId 결과를 덮어쓰지 않도록 취소
    const ctrl = new AbortController()
    ;(async () => {
      try {
        const first = await rawDataService.listUploadHistory(nodeId, 0, pageSize, uploadMethod, ctrl.signal)
        if (ctrl.signal.aborted) return
        setBatches(first)
        setSkip(first.length)
        setHasMore(first.length === pageSize)
      } catch (e: any) {
        if (e?.name === 'AbortError') return
        // 무음 실패: 기존 UX 유지 (에러 메시지 없음)
      }
    })()
    return () => { try { ctrl.abort() } catch {} }
  }, [nodeId, uploadMethod])

  useEffect(() => {
    // 업로드 완료 시 히스토리 새로고침
      const handler = async () => {
        const fresh = await rawDataService.listUploadHistory(nodeId, 0, pageSize, uploadMethod)
      setBatches(fresh)
      setSkip(fresh.length)
      setHasMore(fresh.length === pageSize)
      // 업로드 완료 시 활성 세션 배지 제거
      setActiveSession(null)
    }
    window.addEventListener('upload:completed', handler)
    return () => window.removeEventListener('upload:completed', handler)
  }, [nodeId, uploadMethod])

  // 자동 주기 갱신 제거: 서버 부하 방지 (사용자 동작/업로드 이벤트로만 갱신)

  useEffect(() => {
    // 서버 부하를 줄이기 위해: 활성 업로드가 있을 때만 1회 세션 정보를 조회
    const t = setInterval(() => {
      try {
        const currentId = rawDataService.getActiveSessionId()
        const lastId = lastSessionIdRef.current
        if (currentId !== lastId) {
          lastSessionIdRef.current = currentId
          if (!currentId) {
            setActiveSession(null)
          } else {
            // 단 한 번만 서버에서 batch_id 조회
            const token = localStorage.getItem('access_token')
            if (!token) { setActiveSession(null); return }
            fetch(`${(rawDataService as any)['baseUrl']}/uploads/session`, {
              headers: { 'Authorization': `Bearer ${token}` }
            } as any).then(async (resp) => {
              if (resp.ok) {
                const data = await resp.json()
                if (data && data.active && data.batch_id && data.node_id === nodeId) setActiveSession({ session_id: data.session_id, batch_id: data.batch_id, node_id: data.node_id })
                else setActiveSession(null)
              }
            }).catch(() => {})
          }
        }
      } catch {}
    }, 800)
    return () => clearInterval(t)
  }, [nodeId])

  const loadMoreBatches = async () => {
    const next = await rawDataService.listUploadHistory(nodeId, skip, pageSize, uploadMethod)
    setBatches(prev => [...prev, ...next])
    setSkip(prev => prev + next.length)
    if (next.length < pageSize) setHasMore(false)
  }

  const getBatchDisplayName = (batch: UploadBatchItem): string => {
    if ((uploadMethod === 'auto' || batch.upload_method === 'auto') && batch.trigger_id) {
      // yml 매핑에서 display_name 조회
      const resolved = systemService.resolveTriggerDisplayName(triggerSchemas, batch.trigger_id)
      if (resolved.conditionLabel) return resolved.conditionLabel
      // fallback: trigger_id에서 파싱
      const tokens = batch.trigger_id.split('_').filter(Boolean)
      if (tokens.length >= 4) return `${tokens[2]}_${tokens[3]}`
      if (tokens.length >= 3) return tokens.slice(2).join('_')
      return batch.trigger_id
    }
    return batch.source_filename
  }

  const selectBatch = (batch: UploadBatchItem) => onSelectBatch(batch)

  // 드롭다운 제거에 따라 불필요 코드 제거됨

  const formatDateTime = (iso?: string) => {
    if (!iso) return '-'
    try {
      // 서버는 시간대 표시 없는 UTC 를 내린다 — new Date(문자열) 은 로컬로 읽어 9시간 어긋난다
      const d = parseUTCDate(iso)
      if (!d) return '-'
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const hh = String(d.getHours()).padStart(2, '0')
      const mm = String(d.getMinutes()).padStart(2, '0')
      return `${y}/${m}/${day} ${hh}:${mm}`
    } catch { return iso }
  }

  const isRefreshingRef = useRef(false)
  const refreshHistory = useCallback(async (opts?: { resetPackaging?: boolean; force?: boolean }) => {
    if (isRefreshingRef.current && !opts?.force) return
    isRefreshingRef.current = true

    // 수동 새로고침에서만 다운로드 패키징 폴링을 초기화
    if (opts?.resetPackaging) {
      packagingIntervalsRef.current.forEach(interval => clearInterval(interval))
      packagingIntervalsRef.current.clear()
      setPackagingBatchIds(new Set())
      setDownloadingBatchId(null)
    }

    try {
      const fresh = await rawDataService.listUploadHistory(nodeId, 0, pageSize, uploadMethod)
      setBatches(fresh)
      setSkip(fresh.length)
      setHasMore(fresh.length === pageSize)
    } finally {
      isRefreshingRef.current = false
    }
  }, [nodeId, uploadMethod])

  const STATUS_STEPS = ['UPLOADING','UPLOADED','EXTRACTING','EXTRACTED','PREPROCESSING','COMPLETED'] as const
  const getStepIndex = (status?: string) => {
    const idx = STATUS_STEPS.indexOf((status || '').toUpperCase() as any)
    return idx >= 0 ? idx : 0
  }

  // 실패 이벤트에서 단계/사유를 추출
  const getLastFailure = (history: any[] | undefined | null): { stage?: string; reason?: string; errorCode?: string } | null => {
    if (!Array.isArray(history) || history.length === 0) return null
    for (let i = history.length - 1; i >= 0; i--) {
      const ev = history[i]
      if (ev && (ev.status || '').toLowerCase() === 'failed') {
        const meta = ev.meta || {}
        // Airflow 쪽에서는 reason 또는 message, error_code 또는 errorCode 등 다양한 키를 사용할 수 있으므로 모두 방어적으로 처리
        const reason = meta.reason || meta.message || ''
        const errorCode = meta.error_code || meta.errorCode || ''
        return { stage: ev.stage, reason, errorCode }
      }
    }
    return null
  }

  const stageToStep = (stage?: string): (typeof STATUS_STEPS)[number] | null => {
    const s = (stage || '').toLowerCase()
    if (s === 'extract') return 'EXTRACTING'
    if (s === 'preprocess') return 'PREPROCESSING'
    if (s === 'thumbnail') return 'COMPLETED' // 썸네일 단계는 UI에 전용 스텝이 없으므로 완료 스텝에서 경고 표시
    return null
  }

  const renderStepper = (status?: string, warnStep?: (typeof STATUS_STEPS)[number] | null) => {
    const currentBase = getStepIndex(status)
    // 경고가 있으면 경고 단계만 강조(중복 강조 금지)
    const displayedCurrent = warnStep ? STATUS_STEPS.indexOf(warnStep) : currentBase
    return (
      <div className="flex items-center gap-1 flex-nowrap" aria-label={`status: ${status ?? ''}`}>
        {STATUS_STEPS.map((s, idx) => {
          const isWarn = warnStep === s
          const isCurrent = !isWarn && idx === displayedCurrent
          const baseClass = isWarn
            ? 'bg-yellow-100 text-yellow-800 border-yellow-300'
            : (isCurrent ? 'bg-gray-800 text-white border-gray-800' : 'bg-gray-200 text-gray-600 border-gray-300')
          return (
            <div
              key={s}
              className={`w-[96px] text-center px-2 py-0.5 rounded-full border text-[10px] leading-3 ${baseClass}`}
              title={s}
            >
              {s}
            </div>
          )
        })}
      </div>
    )
  }

  const [downloadingBatchId, setDownloadingBatchId] = useState<number | null>(null)
  const [packagingBatchIds, setPackagingBatchIds] = useState<Set<number>>(new Set())
  // 배치별 패키징 진행률 표시 문자열 (예: "43%")
  const [packagingProgress, setPackagingProgress] = useState<Map<number, string>>(new Map())
  const packagingIntervalsRef = useRef<Map<number, NodeJS.Timeout>>(new Map())
  // 패키징 감시(폴링·완료 알림·자동 다운로드)는 전역 packagingWatcher가 담당한다.
  // 컴포넌트에 폴링을 두면 다른 화면으로 이동하는 순간 죽어서, 완료돼도 알림 없이
  // 버튼만 조용히 돌아와 있었다. 여기서는 감시자 이벤트를 받아 표시만 갱신한다.

  // 행별 삭제 진행 상태 (UX용)
  const [deletingBatchId, setDeletingBatchId] = useState<number | null>(null)

  // 삭제 확인 모달 상태
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; batch: UploadBatchItem | null; typed: string }>({ open: false, batch: null, typed: '' })

  // 폴링 조건: 상태가 COMPLETED가 아니면서 오류(FAILED 또는 실패 이력)가 없는 배치가 하나라도 있을 경우에만 갱신
  const hasIncomplete = batches.some(b => {
    const status = (b.status || '').toUpperCase()
    const notCompleted = status !== 'COMPLETED'
    const isFailed = status === 'FAILED'
    const hasFailedEvent = Array.isArray(b.status_history) && b.status_history.some(ev => (ev && (ev.status || '').toLowerCase() === 'failed'))
    const noError = !isFailed && !hasFailedEvent
    return notCompleted && noError
  })
  // 패키징 중(내가 시작한 것 + 다른 사용자가 시작한 것)에도 목록을 갱신해
  // 모든 화면의 버튼 상태("패키징 중 (n%)" ↔ "다운로드")가 실제를 따라오게 한다
  const hasPackaging = packagingBatchIds.size > 0 || batches.some(b => Boolean(b.packaging?.in_progress))
  useEffect(() => {
    if (!hasIncomplete && !hasPackaging) return
    const id = setInterval(() => { refreshHistory() }, 4000)
    return () => clearInterval(id)
  }, [hasIncomplete, hasPackaging, refreshHistory])

  // 전역 감시자(packagingWatcher) 이벤트 구독 — 버튼 상태·진행률 표기 갱신 전담
  const batchIdOfKey = (key?: string): number | null => {
    if (typeof key !== 'string' || !key.startsWith('raw:')) return null
    const id = Number(key.slice(4))
    return Number.isFinite(id) ? id : null
  }
  useEffect(() => {
    // 마운트 시 감시 중인 배치의 버튼 상태 복원
    const tracked = trackedKeys('raw:')
      .map(k => Number(k.slice(4)))
      .filter(n => Number.isFinite(n))
    if (tracked.length) {
      setPackagingBatchIds(prev => {
        const s = new Set(prev)
        tracked.forEach(id => s.add(id))
        return s
      })
    }

    const onProgress = (e: Event) => {
      const { key, progress, packedBytes } = ((e as CustomEvent).detail || {}) as {
        key?: string; progress?: number; packedBytes?: number
      }
      const batchId = batchIdOfKey(key)
      if (batchId == null) return
      setPackagingBatchIds(prev => (prev.has(batchId) ? prev : new Set(prev).add(batchId)))
      const label = typeof progress === 'number'
        ? `${progress}%`
        : (typeof packedBytes === 'number' ? `${Math.round(packedBytes / 1048576)}MB` : '')
      if (label) setPackagingProgress(prev => new Map(prev).set(batchId, label))
    }
    const onDone = (e: Event) => {
      const { key } = ((e as CustomEvent).detail || {}) as { key?: string }
      const batchId = batchIdOfKey(key)
      if (batchId == null) return
      setPackagingBatchIds(prev => { const s = new Set(prev); s.delete(batchId); return s })
      setPackagingProgress(prev => { const m = new Map(prev); m.delete(batchId); return m })
      refreshHistory()
    }
    window.addEventListener('packaging:progress', onProgress)
    window.addEventListener('packaging:done', onDone)
    return () => {
      window.removeEventListener('packaging:progress', onProgress)
      window.removeEventListener('packaging:done', onDone)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshHistory])

  const handleDownload = async (batchId: number, e: React.MouseEvent) => {
    e.stopPropagation()
    if (downloadingBatchId || packagingBatchIds.has(batchId)) return
    // 다른 사용자가 패키징 중이면 대기 (버튼도 비활성이지만 갱신 지연 대비 이중 방어)
    if (batches.find(x => x.id === batchId)?.packaging?.in_progress) return
    try {
      setDownloadingBatchId(batchId)
      const token = localStorage.getItem('access_token')
      const tokenParam = token ? `?access_token=${encodeURIComponent(token)}` : ''
      const endpoint = `${(rawDataService as any)['baseUrl']}/files/downloads/batch/${batchId}${tokenParam}`
      // 준비 여부만 확인한다. 예전에는 확인용으로 실제 전송을 열어놓고 취소도
      // 하지 않은 채 아래에서 같은 파일을 또 받아 네트워크와 NAS 읽기를 두 번
      // 썼다 (130GB 면 260GB). prepare_only 는 작은 JSON 만 돌려준다.
      const probeUrl = endpoint + (tokenParam ? '&' : '?') + 'prepare_only=true'
      const resp = await fetch(probeUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (resp.status === 202) {
        const data = await resp.json()
        // 완료 시 자동 다운로드할 스냅샷 URL 보관 (서버가 안 주면 기존 endpoint 사용)
        const dlUrl = data.download_url
          ? `${(rawDataService as any)['baseUrl']}${data.download_url}${token ? `&access_token=${encodeURIComponent(token)}` : ''}`
          : endpoint
        // 전역 감시자에 등록 — 어느 화면에 있든 완료되면 알림 + 자동 다운로드
        setPackagingBatchIds(prev => new Set(prev).add(batchId))
        if (data.export_path) {
          const target = batches.find(x => x.id === batchId)
          // 알림에 "무엇이 완료됐는지"를 담는다: 노드 경로 · 파일명 (서버 제공값 우선)
          const parts = [
            data.node_label,
            data.display_name || (target ? (getBatchDisplayName(target) || target.source_filename) : ''),
          ].filter(Boolean)
          const base = (rawDataService as any)['baseUrl']
          trackPackaging(`raw:${batchId}`, {
            statusUrl: `${base}${data.polling_url || `/files/downloads/status?export_path=${encodeURIComponent(data.export_path)}`}`,
            downloadUrl: dlUrl,
            ts: Date.now(),
            kind: '원천데이터',
            name: parts.join('\n'),
          })
        }

        alert(`패키징을 시작했습니다. 완료되면 자동으로 다운로드됩니다.\n예상 크기: ${data.estimated_size_mb || '?'}MB (${data.file_count || '?'}개 파일)`)
        return
      }
      if (!resp.ok) {
        alert(`다운로드 준비 실패: ${resp.status}`)
        return
      }
      // 준비됨 — 서버가 준 스냅샷으로 그 시점 산출물을 정확히 받는다.
      // 대용량 파일은 블랍 변환 없이 브라우저 기본 다운로드로 위임하여 메모리 사용을 피함
      let readySnapshot: string | undefined
      try { readySnapshot = (await resp.json())?.snapshot } catch {}
      const a = document.createElement('a')
      a.href = readySnapshot ? `${endpoint}${tokenParam ? '&' : '?'}snapshot=${readySnapshot}` : endpoint
      a.rel = 'noopener'
      a.click()
    } catch (err) {
      console.error('다운로드 실패', err)
      alert('다운로드에 실패했습니다.')
    } finally {
      setDownloadingBatchId(null)
    }
  }

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        <h4 className="font-medium text-gray-900">업로드 히스토리</h4>
        <button
          onClick={() => onSelectBatch(null)}
          className="text-xs px-2 py-0.5 border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-100"
          title="히스토리 선택을 해제하고 전체를 봅니다."
        >전체 보기</button>
        {/*
        <button
          onClick={() => refreshHistory({ resetPackaging: true, force: true })}
          className="text-xs px-2 py-0.5 border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-100"
          title="히스토리 새로고침"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-refresh-ccw-icon lucide-refresh-ccw">
            <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
            <path d="M3 3v5h5"/>
            <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/>
            <path d="M16 16h5v5"/> 
          </svg>
        </button>
        */}
      </div>

      {batches.length === 0 ? (
        <div className="text-sm text-gray-500">히스토리가 없습니다.</div>
      ) : (
        <div className="divide-y divide-gray-200 border border-gray-200 rounded w-full">
          {batches.map((b, idx) => {
            const isActive = selectedBatchId != null && b.id === selectedBatchId
            const lastFail = getLastFailure(b.status_history)
            const statusUpper = (b.status || '').toUpperCase()
            // 최종 배치 상태가 FAILED 인 경우에만 실패 단계를 강조/표시한다.
            const isFailed = statusUpper === 'FAILED'
            const warnStep = isFailed ? stageToStep(lastFail?.stage) : null
            // 재개는 이어받을 세션이 실제로 살아있을 때만 가능하다.
            // 세션이 죽은 UPLOADING 배치는 재개해도 새 배치가 만들어질 뿐이고,
            // 재개 경로는 중복 체크를 타지 않아 동일 데이터가 이중 등록될 수 있다.
            const isResumable = !!(
              b.status === 'UPLOADING' &&
              activeSession && activeSession.batch_id === b.id && activeSession.node_id === nodeId
            )
            // 세션 없는 UPLOADING 배치는 잔해 — 재개 대신 삭제를 노출
            const isDeadUploading = b.status === 'UPLOADING' && !isResumable
            const isClickable = statusUpper === 'COMPLETED'
            // 패키징 중: 내가 시작한 것(로컬 폴링) 또는 다른 사용자가 시작한 것(서버 job 스캔)
            const isPackaging = packagingBatchIds.has(b.id) || Boolean(b.packaging?.in_progress)
            const packagingLabel = packagingProgress.get(b.id)
              || (b.packaging?.progress != null ? `${b.packaging.progress}%` : '')
            const displayName = getBatchDisplayName(b) || b.source_filename || `Batch-${b.id}`
            const originalName = b.source_filename || ''
            const showOriginalName = displayName !== originalName && Boolean(originalName)
            const handleClick = () => {
              if (!isClickable) return
              if (selectedBatchId != null && b.id === selectedBatchId) {
                onSelectBatch(null) // 토글: 이미 선택된 항목을 다시 클릭하면 해제
              } else {
                selectBatch(b)
              }
            }
            return (
              <div
                key={b.id}
                className={`grid grid-cols-[48px_1fr_600px_120px] items-center gap-3 p-3 ${isClickable ? 'cursor-pointer' : 'cursor-not-allowed'} ${isActive ? 'bg-gray-100' : (isClickable ? 'hover:bg-gray-50' : '')}`} style={{ minWidth: 0 }}
                onClick={handleClick}
                aria-disabled={!isClickable}
                title={isClickable ? '' : '작업 완료 후 확인 가능합니다.'}
              >
                {/* 순번 고정 폭 */}
                <div className={`text-xs text-gray-500 tabular-nums text-center ${!isClickable ? 'opacity-60' : ''}`}>{idx + 1}</div>

                {/* 좌측 정보 고정(1fr) */}
                <div className={`flex items-center gap-2 overflow-hidden ${!isClickable ? 'opacity-60' : ''}`}>
                  <div className="text-sm truncate">
                    <div className="font-medium text-gray-900 truncate" title={showOriginalName ? `${displayName} (${originalName})` : displayName}>
                      {displayName}
                      {b.user_full_name && <span className="ml-2 text-gray-600 font-normal">({b.user_full_name})</span>}
                    </div>
                    {showOriginalName && (
                      <div className="text-xs text-gray-500 truncate">
                        원본: {originalName}
                      </div>
                    )}
                    <div className="text-gray-500 truncate">
                      시작: {formatDateTime(b.started_at)} · 완료: {formatDateTime(b.completed_at)}
                    </div>
                  </div>
                </div>

                {/* 상태 스텝퍼 고정 폭 */}
                <div className="flex items-center justify-end">
                  <div className="w-[600px] pr-2 overflow-x-auto">{renderStepper(b.status, warnStep)}</div>
                </div>

                {/* 우측 액션 고정 폭 */}
                  {/* 재개/삭제 등 누를 수 있는 버튼이 있는 행은 흐림 처리하지 않는다 */}
                  <div className={`flex items-center justify-end gap-2 pl-2 md:pl-3 ${(!isClickable && !isFailed && !isResumable && !isDeadUploading) ? 'opacity-60' : ''}`}>
                  {canDownload && (
                  <button
                    onClick={(e) => handleDownload(b.id, e)}
                    className={`text-xs px-2 py-0.5 border rounded whitespace-nowrap ${
                      !isClickable || deletingBatchId === b.id
                        ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                        : downloadingBatchId === b.id
                        ? 'border-blue-300 bg-blue-50 text-blue-700 cursor-wait'
                        : isPackaging
                        ? 'border-orange-300 bg-orange-50 text-orange-700 cursor-wait'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                    disabled={!isClickable || deletingBatchId === b.id || downloadingBatchId === b.id || isPackaging}
                  >
                    {!isClickable
                      ? '대기 중'
                      : deletingBatchId === b.id
                      ? '삭제 중…'
                      : downloadingBatchId === b.id
                      ? '다운로드 중…'
                      : isPackaging
                      ? `패키징 중…${packagingLabel ? ` (${packagingLabel})` : ''}`
                      : '다운로드'
                    }
                  </button>
                  )}
                  {isClickable && !readOnly && canDelete && (
                    <button
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        if (deletingBatchId != null) return
                        setConfirmDelete({ open: true, batch: b, typed: '' }) 
                      }}
                      className={`text-xs px-2 py-0.5 border rounded whitespace-nowrap ${
                        deletingBatchId === b.id
                          ? 'border-red-300 bg-red-50 text-red-400 cursor-wait'
                          : 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
                      }`}
                      title="해당 히스토리와 포함된 파일들을 삭제합니다."
                      disabled={deletingBatchId === b.id}
                    >
                      {deletingBatchId === b.id ? '삭제 중…' : '삭제'}
                    </button>
                  )}
                  {(isFailed || isDeadUploading) && !readOnly && canDelete && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation()
                        try {
                          if (deletingBatchId != null) return
                          setDeletingBatchId(b.id)
                          await rawDataService.deleteUploadBatch(b.id)
                          if (selectedBatchId === b.id) onSelectBatch(null)
                          await refreshHistory({ force: true })
                          try { onAfterDelete && onAfterDelete(b.id) } catch {}
                        } catch (err: any) {
                          alert(err?.message || '삭제에 실패했습니다.')
                        } finally {
                          setDeletingBatchId(prev => prev === b.id ? null : prev)
                        }
                      }}
                      className={`text-xs px-2 py-0.5 border rounded whitespace-nowrap ${
                        deletingBatchId === b.id
                          ? 'border-red-300 bg-red-50 text-red-400 cursor-wait'
                          : 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
                      }`}
                      title="실패한 히스토리를 즉시 삭제합니다."
                      disabled={deletingBatchId === b.id}
                    >삭제</button>
                  )}
                  {isResumable && !readOnly && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        if (onResume) onResume(b.source_filename_raw || b.source_filename)
                      }}
                      className="text-xs text-yellow-700 bg-yellow-100 border border-yellow-200 px-2 py-0.5 rounded whitespace-nowrap"
                      title="중단된 업로드를 재개합니다. 동일한 파일을 선택하세요."
                    >업로드 재개</button>
                  )}
                </div>
                {/* 실패 사유 (최종 상태가 FAILED 인 경우에만 표시) */}
                {isFailed && lastFail && (
                  <div className="col-span-full px-3 pt-1">
                    <div className="text-xs text-red-600 text-center">
                      {(() => {
                        const code = (lastFail.errorCode || '').toString().toUpperCase()
                        const reason = (lastFail.reason || '').toString()
                        // 주요 에러코드별 사용자 친화적인 설명
                        if (code === 'EXTRACT_E_BAD_CONTENT') {
                          return '이미지 파일이 아닌 파일이 많아 작업이 중지됐습니다.'
                        }
                        if (code === 'EXTRACT_E101') {
                          return '압축 파일 안에서 유효한 이미지 파일을 찾지 못해 작업이 중지되었습니다.'
                        }
                        if (code === 'EXTRACT_E199') {
                          return reason
                            ? `압축 해제 중 오류가 발생했습니다: ${reason}`
                            : '압축 해제 중 알 수 없는 오류가 발생했습니다.'
                        }
                        if (code === 'PREPROC_P101') {
                          return '전처리할 대상 이미지가 없어 전처리 작업을 수행하지 못했습니다.'
                        }
                        if (code === 'THUMB_S003') {
                          return '썸네일을 만들 이미지가 없어 썸네일 생성이 건너뛰어졌습니다.'
                        }
                        if (code === 'THUMB_T101') {
                          return '썸네일 생성 대상 이미지를 하나도 처리하지 못해 작업이 실패했습니다.'
                        }
                        if (code === 'THUMB_T102') {
                          return '썸네일 생성 대상 중 일부만 처리되어 작업이 실패했습니다. (일부 원본 이미지가 손상되었거나 찾을 수 없습니다.)'
                        }
                        if (code === 'THUMB_T103') {
                          return reason
                            ? `썸네일 생성 중 오류가 발생했습니다: ${reason}`
                            : '썸네일 생성 중 알 수 없는 오류가 발생했습니다.'
                        }
                        // 그 외: 코드/사유를 그대로 노출하여 디버깅 가능하게 함
                        if (code && reason) {
                          return `작업이 실패했습니다. (코드: ${code}, 사유: ${reason})`
                        }
                        if (code) {
                          return `작업이 실패했습니다. (코드: ${code})`
                        }
                        if (reason) {
                          return `작업이 실패했습니다. (${reason})`
                        }
                        return '작업이 실패했습니다.'
                      })()}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
          {hasMore && (
            <div className="p-2 flex justify-center bg-white">
              <button className="px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50" onClick={loadMoreBatches}>히스토리 더보기</button>
            </div>
          )}
        </div>
      )}
      {/* 삭제 확인 모달 */}
      {confirmDelete.open && confirmDelete.batch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" role="dialog" aria-modal="true">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-3">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="text-base font-semibold text-gray-900">히스토리 삭제</div>
            </div>
            <div className="px-4 py-3 space-y-3">
              <p className="text-sm text-gray-700 select-text">
                '{confirmDelete.batch.source_filename}' 업로드 히스토리를 영구적으로 삭제하시겠습니까?
              </p>
              <div>
                <div className="text-xs text-gray-600 mb-2">삭제를 확인하려면 아래 텍스트 입력 필드에 '영구 삭제'를 입력하세요.</div>
                <input
                  autoFocus
                  type="text"
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                  placeholder="영구 삭제"
                  value={confirmDelete.typed}
                  onChange={(e) => setConfirmDelete(prev => ({ ...prev, typed: e.target.value }))}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      const required = '영구 삭제'
                      if (confirmDelete.typed.trim() !== required) return
                      try {
                        await rawDataService.deleteUploadBatch(confirmDelete.batch!.id)
                        if (selectedBatchId === confirmDelete.batch!.id) onSelectBatch(null)
                        await refreshHistory({ force: true })
                        try { onAfterDelete && onAfterDelete(confirmDelete.batch!.id) } catch {}
                      } catch (err: any) {
                        alert(err?.message || '삭제에 실패했습니다.')
                      } finally {
                        setConfirmDelete({ open: false, batch: null, typed: '' })
                      }
                    } else if (e.key === 'Escape') {
                      setConfirmDelete({ open: false, batch: null, typed: '' })
                    }
                  }}
                />
              </div>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
              <button
                className="text-sm px-3 py-1 border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-50"
                onClick={() => setConfirmDelete({ open: false, batch: null, typed: '' })}
              >취소</button>
              <button
                className="text-sm px-3 py-1 rounded text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={(confirmDelete.typed.trim() !== '영구 삭제') || deletingBatchId != null}
                onClick={async () => {
                  const required = '영구 삭제'
                  if (confirmDelete.typed.trim() !== required) return
                  try {
                    if (deletingBatchId != null) return
                    setDeletingBatchId(confirmDelete.batch!.id)
                    await rawDataService.deleteUploadBatch(confirmDelete.batch!.id)
                    if (selectedBatchId === confirmDelete.batch!.id) onSelectBatch(null)
                    await refreshHistory({ force: true })
                    try { onAfterDelete && onAfterDelete(confirmDelete.batch!.id) } catch {}
                  } catch (err: any) {
                    alert(err?.message || '삭제에 실패했습니다.')
                  } finally {
                    setDeletingBatchId(prev => prev === confirmDelete.batch!.id ? null : prev)
                    setConfirmDelete({ open: false, batch: null, typed: '' })
                  }
                }}
              >영구 삭제</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// 삭제 확인 모달 UI는 본 파일 하단에 함께 정의 (간단한 오버레이)


